/** Taxonomy domain helpers shared by the tree editor and the audio form. */

// Firmware-coupled EMS programs. Adding one needs a coordinated backend + app
// release - the app's SessionType -> EmsMode switch is exhaustive - not a deploy.
// Mirrors ESessionType in CoreApi session/constants.py; keep the two in sync.
export const SESSION_TYPES = [
  'trial',
  'wake_up_session',
  'unwind',
  'nap',
  'relax',
  'meditation',
  'overnight_sleep',
  'sleep_readiness',
];

export const CHILD_LAYOUTS = ['grid_2col', 'audio_list'];

export const DEFAULT_CHILD_LAYOUT = 'audio_list';

// Contract: an unknown child_layout renders as audio_list; never crash on it.
export const normalizeChildLayout = (value) =>
  CHILD_LAYOUTS.includes(value) ? value : DEFAULT_CHILD_LAYOUT;

export const humanize = (value) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Locate a node plus the sibling list it lives in - reorder needs both. */
export const findNodeContext = (nodes, id, parentId = null) => {
  const siblings = nodes || [];
  const index = siblings.findIndex((node) => node.id === id);
  if (index !== -1) return { node: siblings[index], parentId, siblings, index };
  for (const node of siblings) {
    const found = findNodeContext(node.children, id, node.id);
    if (found) return found;
  }
  return null;
};

/** Publish policy: a childless node with nothing filed under it is a tappable dead end. */
// Queue rows carry `has_children`; tree nodes carry `children`.
export const isEmptyLeaf = (node) =>
  !(node?.has_children || (node?.children || []).length) && node?.audio_count === 0;

export const EMPTY_LEAF_MESSAGE =
  'Publish is blocked: an empty leaf is a tappable dead end. Add at least one audio first.';

export const COMING_SOON_MESSAGE =
  'Shown in the app as a disabled Coming Soon tile even while unpublished or empty, as long as every ancestor is published.';

/** Only a grid layout with children renders children; everything else renders the audio list. */
export const rendersAudioList = (node) =>
  normalizeChildLayout(node?.child_layout) !== 'grid_2col' || (node?.children || []).length === 0;

export const countDescendants = (node) =>
  (node?.children || []).reduce((total, child) => total + 1 + countDescendants(child), 0);

/** Root-to-node chain, or null when the id isn't in the forest. */
export const findNodePath = (nodes, id) => {
  for (const node of nodes || []) {
    if (node.id === id) return [node];
    const below = findNodePath(node.children, id);
    if (below) return [node, ...below];
  }
  return null;
};

/** Guard against dropping a node into its own subtree. */
export const containsNode = (node, id) =>
  (node?.children || []).some((child) => child.id === id || containsNode(child, id));

export const flattenTree = (nodes, parentId = null, depth = 0, ancestors = []) =>
  (nodes || []).flatMap((node) => {
    const pathTitles = [...ancestors, node.title || node.key];
    const children = node.children || [];
    return [
      { node, depth, parentId, pathTitles, hasChildren: children.length > 0 },
      ...flattenTree(children, node.id, depth + 1, pathTitles),
    ];
  });

export const pathLabel = (pathTitles) => (pathTitles || []).join(' → ');
